import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";

import { Button, Alert } from "reactstrap";

import { logError } from "../../../telemetry";
import { GoogleLoginConfigError, GoogleScriptLoadError } from "../types/errors";

interface LoginErrorBoundaryProps {
  children: ReactNode;
  fallbackMessage?: string;
  onRetry?: () => void;
}

interface LoginErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class LoginErrorBoundary extends Component<
  LoginErrorBoundaryProps,
  LoginErrorBoundaryState
> {
  constructor(props: LoginErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): LoginErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Login component error:", error, errorInfo);
    logError(error, {
      component: "LoginErrorBoundary",
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
      additionalContext: {
        location: window.location.href,
        timestamp: new Date().toISOString(),
        errorType: error.name,
      },
    });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  renderErrorMessage(): { title: string; message: string } {
    const { error } = this.state;
    const { fallbackMessage } = this.props;

    if (error instanceof GoogleLoginConfigError) {
      return {
        title: "Login Service Error",
        message: "Please contact an administrator to fix this issue.",
      };
    }

    if (error instanceof GoogleScriptLoadError) {
      return {
        title: "Login Service Unavailable",
        message:
          "We couldn't connect to the authentication service. Please try again.",
      };
    }

    return {
      title: "Login Error",
      message: fallbackMessage || "There was a problem with the login process.",
    };
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      const { title, message } = this.renderErrorMessage();

      return (
        <div className="login-error-boundary p-3 text-center">
          <Alert color="danger">
            <h4>{title}</h4>
            <p>{message}</p>
            <Button color="primary" onClick={this.handleRetry}>
              Try Again
            </Button>
          </Alert>
        </div>
      );
    }

    return children;
  }
}
